import { merge } from 'lodash'
import { v4 as generateUniqueId } from 'uuid'

import type {
  CdmClient,
  CdmClientEmploymentIncome,
  CdmClientIncome,
  CdmUnearnedIncome,
} from '../../service/luther/model'
import { getCountryCodeFromNameOrDemonym, getCountryName } from '../countries/countries'

// TODO This file and its friends in this directory should be unit tested (RT 25/11/24)

// When entity has an id Apollo by default uses that for caching.
// If id does not exist there are silent bugs.
// Prod issue was users seeing correct amount of entities but with same values,
// i.e. if 5 Debts exists on client and all 5 ids are id: null, Apollo will return 5 items with data of the 1st item
// To avoid that keyFields policy needs to specify key that can be used in case id is missing.
// In most nested entities we do not have unique value keys so this MR introduces internalHash that is set if id is missing and removed before sending payload to BE. internalHash is not used anywhere and is only needed for cashing.
export const addInternalHash = <T extends { id?: string | never; [key: string]: any }>(
  arr?: Maybe<T[]>,
  parentId?: Maybe<string>,
) =>
  arr?.map((item) => {
    const itemId = parentId ? `${parentId}_${item.id}` : item.id
    return {
      ...(item || {}),
      internalHash: item?.id ? itemId : generateUniqueId(), // TODO Can we use consistent, hash-based IDs rather than random GUIDs? (RT 25/11/24)
    }
  }) || null

export const removeInternalHash = <T extends { internalHash?: string | null }>(arr?: T[] | undefined | null) =>
  arr
    ? arr.map((item) => {
        if (item?.internalHash) {
          delete item.internalHash
        }
        return item
      })
    : arr

export const formatClient = <T extends CdmClient>(client: T) => {
  const { details } = client

  const nationalities =
    details?.nationalities && details.nationalities.length > 0
      ? details.nationalities.reduce((acc, countryCode) => {
          const countryName = getCountryName(countryCode)
          if (countryName) {
            return [...acc, countryName]
          }
          return acc
        }, [] as string[])
      : null

  const country_of_birth = details?.country_of_birth ? getCountryName(details.country_of_birth) : null

  const mapUnearnedIncome = (unearnedIncome: CdmUnearnedIncome): CdmUnearnedIncome => ({
    ...unearnedIncome,
    frequency:
      unearnedIncome.frequency && unearnedIncome.frequency !== 'INVALID_FREQUENCY'
        ? unearnedIncome.frequency
        : 'INVALID_FREQUENCY',
    type:
      unearnedIncome.type && unearnedIncome.type !== 'INVALID_UNEARNED_INCOME_TYPE'
        ? unearnedIncome.type
        : 'INVALID_UNEARNED_INCOME_TYPE',
    amount: unearnedIncome.amount,
    other_type: unearnedIncome.other_type,
  })

  const unearnedIncome = details?.unearned_income && addInternalHash(details.unearned_income.map(mapUnearnedIncome))
  const anticipatedRetirementUnearnedIncome =
    details?.anticipated_retirement_unearned_income &&
    addInternalHash(details.anticipated_retirement_unearned_income.map(mapUnearnedIncome))

  const mapIncomeAndEmployment = (item: CdmClientIncome): CdmClientIncome => ({
    ...item,
    employment:
      item.employment &&
      ({
        ...item.employment,
        total_guaranteed_annual: item.employment.total_guaranteed_annual,
        total_additional_annual: item.employment.total_additional_annual,
        incomes:
          item.employment.incomes &&
          item.employment.incomes.map((income) => ({
            ...income,
            income_history: income.income_history && income.income_history.map((value) => Number(value.toString())),
          })),
      } as CdmClientEmploymentIncome),
  })
  const incomeAndEmployment =
    details?.income_and_employment && addInternalHash(details.income_and_employment.map(mapIncomeAndEmployment))

  const anticipatedRetirementIncome =
    details?.anticipated_retirement_income &&
    addInternalHash(details.anticipated_retirement_income.map(mapIncomeAndEmployment))

  return {
    id: client.client_id || '',
    ...client,
    ...(client.details && {
      details: {
        ...details,
        accounts: addInternalHash(details?.accounts) ?? [],
        addresses: addInternalHash(details?.addresses, client.client_id),
        anticipated_retirement_income: anticipatedRetirementIncome || null,
        anticipated_retirement_unearned_income: anticipatedRetirementUnearnedIncome || null,
        id: client.client_id,
        middle_name: details?.middle_name || null,
        country_of_birth: country_of_birth || null,
        nationalities: nationalities || null,
        retirement_age: details?.retirement_age || null,
        id_verification_status: details?.id_verification_status || null,
        debts: details?.debts ? addInternalHash(details?.debts) : null,
        deposits: details?.deposits ? addInternalHash(details?.deposits) : null,
        credit_history_bankruptcy_events: details?.credit_history_bankruptcy_events
          ? addInternalHash(details?.credit_history_bankruptcy_events)
          : null,
        credit_history_county_court_judgements: details?.credit_history_county_court_judgements
          ? addInternalHash(details?.credit_history_county_court_judgements)
          : null,
        mortgage_application_refused: details?.mortgage_application_refused
          ? addInternalHash(details?.mortgage_application_refused)
          : null,
        credit_history_debt_management_plans: details?.credit_history_debt_management_plans
          ? addInternalHash(details?.credit_history_debt_management_plans)
          : null,
        credit_history_individual_voluntary_arrangements: details?.credit_history_individual_voluntary_arrangements
          ? addInternalHash(details?.credit_history_individual_voluntary_arrangements)
          : null,
        previous_names: details?.previous_names ? addInternalHash(details?.previous_names) : null,
        dependants: details?.dependants ? addInternalHash(details?.dependants) : null,
        unearned_income: unearnedIncome || null,
        income_and_employment: incomeAndEmployment || null,
        date_of_birth: details?.date_of_birth || null,
        smoker_status: details?.smoker_status !== undefined ? details?.smoker_status : null,
        client_weight: details?.client_weight || null,
        client_height: details?.client_height || null,
        display_weight_unit: details?.display_weight_unit || null,
        display_height_unit: details?.display_height_unit || null,
        ...('health_information' in (details ?? {}) && {
          health_information: details?.health_information
            ? formatClientHealthInformation(details.health_information)
            : null,
        }),
      } as Client,
    }),
  } as ClientVersion
}

export const formatClientAsRequestBody = (input: ClientInput): ClientInput => {
  const mapEmploymentIncome = (item: ClientIncomeInput): ClientIncomeInput => ({
    ...item,
    employment: item.employment && {
      ...item.employment,
      incomes:
        item.employment.incomes &&
        item.employment.incomes.map((income) => ({
          ...income,
          income_amount: income.income_amount,
          income_history: income.income_history && income.income_history.map((value) => Number(value.toString())),
        })),
    },
  })

  const mapUnearnedIncome = (unearnedIncome: UnearnedIncomeInput): UnearnedIncomeInput => ({
    ...unearnedIncome,
    frequency:
      unearnedIncome.frequency && unearnedIncome.frequency !== 'INVALID_FREQUENCY'
        ? unearnedIncome.frequency
        : 'INVALID_FREQUENCY',
    type:
      unearnedIncome.type && unearnedIncome.type !== 'INVALID_UNEARNED_INCOME_TYPE'
        ? unearnedIncome.type
        : 'INVALID_UNEARNED_INCOME_TYPE',
    amount: unearnedIncome.amount,
    other_type: unearnedIncome.other_type,
  })

  const incomeAndEmployment = input.income_and_employment && input.income_and_employment.map(mapEmploymentIncome)
  const retirementIncomeAndEmployment =
    input.anticipated_retirement_income && input.anticipated_retirement_income.map(mapEmploymentIncome)
  const unearnedIncome = input.unearned_income && input.unearned_income.map(mapUnearnedIncome)
  const unearnedRetirementIncome =
    input.anticipated_retirement_unearned_income && input.anticipated_retirement_unearned_income.map(mapUnearnedIncome)

  return {
    ...input,
    debts: removeInternalHash(input.debts),
    deposits: removeInternalHash(input.deposits),
    unearned_income: removeInternalHash(unearnedIncome),
    credit_history_bankruptcy_events: removeInternalHash(input.credit_history_bankruptcy_events),
    credit_history_county_court_judgements: removeInternalHash(input.credit_history_county_court_judgements),
    mortgage_application_refused: removeInternalHash(input.mortgage_application_refused),
    credit_history_debt_management_plans: removeInternalHash(input.credit_history_debt_management_plans),
    credit_history_individual_voluntary_arrangements: removeInternalHash(
      input.credit_history_individual_voluntary_arrangements,
    ),
    previous_names: removeInternalHash(input.previous_names),
    dependants: removeInternalHash(input.dependants),
    accounts: removeInternalHash(input.accounts),
    addresses: removeInternalHash(input.addresses),
    income_and_employment: removeInternalHash(incomeAndEmployment),
    anticipated_retirement_income: removeInternalHash(retirementIncomeAndEmployment),
    anticipated_retirement_unearned_income: removeInternalHash(unearnedRetirementIncome),
  }
}

// We must use a Set to remove duplicates from the array of nationalities
// This is because variations exist between sources e.g. "British" vs "United Kingdom"
// Both are valid and will be parsed as "GB" by the getCountryCodeFromNameOrDemonym function
export const formatNationalities = (srcObject: string[]) =>
  Array.from(new Set(srcObject.map(getCountryCodeFromNameOrDemonym)))

const formatClientHealthInformation = (healthInformation: ClientHealthInfo) => {
  return merge(
    {
      medical_appointment_in_last_twelve_months: {
        is_applicable: null,
        additional_details: null,
      },
      planned_medical_appointments_or_test_results: {
        is_applicable: null,
        additional_details: null,
      },
      current_medication_intake: {
        is_applicable: null,
        additional_details: null,
      },
      client_has_no_condition: null,
      high_blood_pressure: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
        systolic: null,
        diastolic: null,
      },
      heart_disease: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
      },
      heart_attack: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
      },
      heart_valve_replacement: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
      },
      diabetes: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
        latest_hba1c_level: null,
      },
      stroke: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
      },
      mini_stroke: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
      },
      cancer_diagnosis: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
      },
      cancer_requiring_surgery: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
      },
      parkinson: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
      },
      dementia: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
      },
      multiple_sclerosis: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
      },
      chronic_kidney_failure: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
      },
      chronic_respiratory_disease: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
      },
      organ_transplant: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
      },
      cirrhosis: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
      },
      motor_neurone_disease: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
      },
      peripheral_vascular_disease: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
      },
      hepatitis_c: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
      },
      hiv: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
        },
      },
      depression: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
          time_taken_off: null,
          prescribed_medication: null,
        },
      },
      anxiety_or_panic: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
          time_taken_off: null,
          prescribed_medication: null,
        },
      },
      bipolar_psychosis_schizophrenia: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
          time_taken_off: null,
          prescribed_medication: null,
        },
      },
      other_mental_health_condition: {
        has_condition: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
          time_taken_off: null,
          prescribed_medication: null,
        },
      },

      muscle_bone_joint_condition: {
        has_condition: null,
        had_physiotherapy_surgery_injections: null,
        condition_details: {
          date_of_diagnosis: null,
          current_or_planned_treatments: null,
          additional_details: null,
          time_taken_off: null,
          prescribed_medication: null,
        },
      },
      other_medical_condition: {
        is_applicable: null,
        additional_details: null,
      },
      early_retirement_due_to_ill_health: {
        is_applicable: null,
        additional_details: null,
      },
      medical_history_additional_details: null,

      client_has_no_family_medical_history: null,
      family_heart_attack: {
        is_applicable: null,
        additional_details: null,
      },
      family_cardiomyopathy: {
        is_applicable: null,
        additional_details: null,
      },
      family_diabetes: {
        is_applicable: null,
        additional_details: null,
      },
      family_cancer: {
        is_applicable: null,
        additional_details: null,
      },
      family_muscular_dystrophy: {
        is_applicable: null,
        additional_details: null,
      },
      family_neurological_disorder: {
        is_applicable: null,
        additional_details: null,
      },
      family_kidney_disease: {
        is_applicable: null,
        additional_details: null,
      },
      family_other_family_medical_condition: {
        is_applicable: null,
        additional_details: null,
      },
      family_medical_history_do_not_know: null,
      family_medical_history_additional_details: null,

      gp_registration_status: null,
      gp_has_access_to_english_medical_records: {
        is_applicable: null,
        additional_details: null,
      },
      gp_name: null,
      gp_phone_number: null,
      gp_address: null,
      gp_additional_notes: null,

      alcohol_consumption_units_per_week: null,
      alcohol_consumption_gp_referral: {
        is_applicable: null,
        additional_details: null,
      },
      has_smoked_in_the_past: null,
      stopped_smoking_date: null,
      cigarettes_consumption: {
        is_applicable: null,
        daily_consumption: null,
      },
      vape_consumption: {
        is_applicable: null,
        daily_consumption: null,
      },
      rolling_tobacco_consumption: {
        is_applicable: null,
        daily_consumption: null,
      },
      other_nicotine_consumption_details: {
        is_applicable: null,
        additional_details: null,
      },
      recreational_drug_use: {
        is_applicable: null,
        additional_details: null,
      },
      motorbike_for_transport: {
        is_applicable: null,
        additional_details: null,
      },
      hazardous_sports_or_hobbies: {
        is_applicable: null,
        additional_details: null,
      },
      receives_payment_for_sport_participation: {
        is_applicable: null,
        additional_details: null,
      },
    },
    healthInformation,
  )
}
